/** @type sweetAlert.Swal */
import Swal from 'sweetalert2/dist/sweetalert2.js'
import lang from '@/plugins/lang'

/**
 * @enum {Swal.DismissReason}
 */
const DismissReason = {
    CANCEL: 'cancel',
    BACKDROP: 'backdrop',
    CLOSE: 'close',
    ESC: 'esc',
}

export function fireConfirmDelete(options = {}) {
  let title = options.nameItem ?
      lang.t('alert:confirm_delete.title', { replace: { item: options.nameItem }, context: 'particular', interpolation: { escapeValue: false } }) :
      lang.t('alert:confirm_delete.title')

  return fireConfirmDeactivate({
      title,
      text: lang.t('alert:confirm_delete.text'),
      confirmButtonText: lang.t('alert:confirm_delete.button_confirm'),
      textSuccess: lang.t('alert:confirm_delete.text_success', { replace: { item: options.nameItem || '' } }),
      ...options
  })
}

export function fireConfirmDeactivate({ nameItem, textSuccess, successAlertCallback, ...options} = {}) {
    let title = nameItem ?
        lang.t('alert:confirm_deactivate.title', { replace: { item: nameItem }, context: 'particular', interpolation: { escapeValue: false } }) :
        lang.t('alert:confirm_deactivate.title')

    return Swal.fire({
        title,
        text: lang.t('alert:confirm_deactivate.text'),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        confirmButtonText: lang.t('alert:confirm_deactivate.button_confirm'),
        cancelButtonText: lang.t('button_cancel'),
        showLoaderOnConfirm: true,
        backdrop: true,
        allowOutsideClick: () => !Swal.isLoading(),
        allowEscapeKey: () => !Swal.isLoading(),
        ...options,
        preConfirm(response) {
            Swal.getCancelButton().style.display = "none"

            return options?.preConfirm(response) || response
        }
    }).then(result => {
        if (result.isConfirmed) {
            Swal.fire({
                title: lang.t('alert:successful_action_title'),
                text: textSuccess || lang.t('alert:confirm_deactivate.text_success', { replace: { item: nameItem || '' } }),
                icon: 'success'
            })
                .then(() => {
                    if (typeof successAlertCallback === 'function') {
                        successAlertCallback()
                    }
                })
        }
    })
}

export function fireConfirmRestore({ nameItem, ...options } = {}) {
  let title = nameItem ?
      lang.t('alert:confirm_restore.title', { replace: { item: nameItem }, context: 'particular', interpolation: { escapeValue: false } }) :
      lang.t('alert:confirm_restore.title')

  return fireConfirmWarning({
      title,
      text: lang.t('alert:confirm_restore.text'),
      confirmButtonText: lang.t('alert:confirm_restore.button_confirm'),
      textSuccess: lang.t('alert:confirm_restore.text_success', { replace: { item: nameItem || '' } }),
      ...options
  })
}

export function fireConfirmWarning({ titleSuccess, textSuccess, successAlertCallback, reload, ...options} = {}) {
    let preConfirmResult = null

    return Swal.fire({
        title: lang.t('alert:confirm_warning_action.title'),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        confirmButtonText: lang.t('alert:confirm_warning_action.button_confirm'),
        cancelButtonText: lang.t('button_cancel'),
        showLoaderOnConfirm: true,
        backdrop: true,
        allowOutsideClick: () => !Swal.isLoading(),
        allowEscapeKey: () => !Swal.isLoading(),
        ...options,
        preConfirm(response) {
          Swal.getCancelButton().style.display = "none"

          return options?.preConfirm(response).then(data => preConfirmResult = data)
        }
    }).then(result => {
        if (result.isConfirmed) {
          Swal.fire({
            title: titleSuccess || lang.t('alert:successful_action_title'),
            text: textSuccess || lang.t('alert:successful_action_text'),
            icon: 'success'
          })
          .then(() => {
            if(reload) {
                window.location.reload()

                return
            }

            if (typeof successAlertCallback === 'function') {
                successAlertCallback(preConfirmResult)
            }
          })
        }
    })
}

export function fireSaveRedirectSuccess({ urlRedirect, ...options } = {}) {
  return Swal.fire({
    title: lang.t('alert:successful_action_title'),
    text: lang.t('alert:save_success.text'),
    icon: 'success',
    cancelButtonColor: '#09B785',
    ...options,
  }).then(result => {
    if (!result.isConfirmed) {
      window.location.href = urlRedirect
      return
    }
  });
}

export function fireSaveSuccess(options = {}) {
    return Swal.fire({
        title: lang.t('alert:successful_action_title'),
        text: lang.t('alert:save_success.text'),
        icon: 'success',
        ...options
    });
}

export function fireEditSuccess(options = {}) {
    return Swal.fire({
        title: lang.t('alert:successful_action_title'),
        text: lang.t('alert:edit_success.text'),
        icon: 'success',
        ...options
    });
}

export function fireActionSuccess(options = {}) {
    return Swal.fire({
        title: lang.t('alert:successful_action_title'),
        text: lang.t('alert:successful_action_text'),
        icon: 'success',
        ...options
    });
}

export function fireConfirmOrDeny({ titleSuccess, textSuccess, htmlSuccess, ...options} = {}) {
    return Swal.fire({
        icon: 'question',
        showDenyButton: true,
        denyButtonColor: '#d33',
        showLoaderOnConfirm: true,
        showLoaderOnDeny: true,
        backdrop: true,
        allowOutsideClick: () => !Swal.isLoading(),
        allowEscapeKey: () => !Swal.isLoading(),
        ...options,
        preConfirm(response) {
            Swal.getDenyButton().style.display = "none"

            return options?.preConfirm(response)
        },
        preDeny(response) {
            Swal.getConfirmButton().style.display = "none"

            return options?.preDeny(response)
        }
    }).then(result => {
        if ((result.isConfirmed || result.isDenied) && (textSuccess || htmlSuccess)) {
            Swal.fire({
                title: titleSuccess || lang.t('alert:successful_action_title'),
                text: textSuccess,
                html: htmlSuccess,
                icon: 'success'
            })
        }
    })
}

export { DismissReason, Swal }
